/**
 * Store Pinia des agents (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import AgentServices from "../services/AgentService";
import { IAgent } from "../models/agent";
import { IAgentForm } from "../models/forms/agentForm";
import { IAgentApiResponse } from "../models/api/agentApiResponse";
import { IAgentFilter } from "../models/filters/agentFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { ICenter } from "../models/center";
import { useCenterStore } from "./center.store";
import signalRManager from "../helper/signalRManager";
import i18n from "@/i18n";
import IPayloadStore from "../models/api/payloadStore";

/**
 * Store Pinia des agents utilisant la Composition API
 */
export const useAgentStore = defineStore("agents", () => {
    // State
    const datas = ref<IAgent[]>([]);
    const current = ref<IAgent>({} as IAgent);
    const itemForm = ref<IAgentForm>({} as IAgentForm);

    // Getters
    /**
     * Récupère un agent par son identifiant dans le store, sans passer par l'api
     */
    const getAgentById = computed(() => (id: number): IAgent | undefined => {
        return datas.value?.find((agent) => agent.id == id);
    });

    /**
     * Récupère un agent par son code dans le store, sans passer par l'api
     */
    const getAgentByCode = computed(() => (code: string): IAgent | undefined => {
        return datas.value?.find((agent) => agent.code == code && code != '');
    });

    const isCodeDisponible = computed(() => (code: string, id: number): boolean => {
        // On vérifie uniquement parmi les agents actifs (sans date de fin).
        // Un agent inactif (endDate renseignée) ne bloque pas la création d'un nouveau code,
        // mais si l'agent inactif est réactivé, le watch sur endDate dans AgentDetail.vue
        // détectera le conflit avec l'agent actif créé entre-temps.
        return datas.value.find((item) => item.id != id && item.code == code && !item.endDate) == undefined;
    });

    /**
     * Récupère un agent par son code et fonction dans le store, sans passer par l'api
     */
    const getAgentByCodeAndFunction = computed(() => (code: string, isLabAssist: boolean, isSampler: boolean): IAgent | undefined => {
        return datas.value?.find((agent) =>
            agent.code == code && code != '' &&
            agent.endDate == undefined &&
            (!isLabAssist || agent.isLabAssist == isLabAssist) &&
            (!isSampler || agent.isSampler == isSampler)
        );
    });

    const getAgentSamplerActifs = computed(() => {
        if (datas.value)
            return datas.value.filter((agent) => agent.isSampler && agent.endDate == undefined);
        else
            return undefined;
    });

    const getAgentLabAssistActifs = computed(() => {
        return datas.value?.filter((agent) => agent.isLabAssist && agent.endDate == undefined);
    });

    const getAgentActifs = computed(() => {
        return datas.value?.filter((agent) => agent.endDate == undefined);
    });

    // Actions

    /**
     * Hydrate une liste d'agents en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param agents Liste d'agents reçus de l'API avec des IDs
     * @returns Liste d'agents avec les références des stores
     */
    async function hydrateAgents(agents: IAgentApiResponse[]): Promise<IAgent[]> {
        if (agents.length === 0) return [];

        // 1. Récupérer les stores une seule fois
        const centerStore = useCenterStore();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];

        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps d'index pour des lookups O(1)
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));

        // 4. Hydrater tous les agents en une seule passe
        const models: IAgent[] = [];

        for (const agent of agents) {
            const model = { ...agent } as unknown as IAgent;

            // Lier les références (lookup O(1) via Map)
            model.center = centerMap.get(agent.centerId)!;

            models.push(model);
        }

        return models;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: IAgentFilter) {
        let result = await AgentServices.getAll<IAgentFilter, IAgentApiResponse>(filter);
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateAgents(result.data as IAgentApiResponse[]);
        }

        return result;
    }

    /**
     * Récupération d'un agent par son identifiant
     * @param id
     */
    async function get(id: number) {
        let result = await AgentServices.getById<IAgentApiResponse>(id);

        if (!result.error) {
            const [hydratedAgent] = await hydrateAgents([result.data as IAgentApiResponse]);

            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = hydratedAgent;

            current.value = hydratedAgent;
            itemForm.value = toForm(hydratedAgent);
        }

        return result;
    }

    /**
     * Récupération de l'objet dans le store
     * @param id Identifiant de l'objet
     */
    async function searchStoreById(id: number): Promise<IAgent> {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IAgent);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        var timestamp = Date.parse(itemForm.value.endDate?.toString() || '');

        if (isNaN(timestamp)) {
            itemForm.value.endDate = undefined;
        }

        let result = {} as ServiceOperationResult<IAgentApiResponse | IDataOperationResult<IAgentApiResponse>>;

        if (itemForm.value.id > 0) {
            result = await AgentServices.update(itemForm.value);

            if (!result.error) {
                const [hydratedAgent] = await hydrateAgents([result.data as IAgentApiResponse]);
                current.value = hydratedAgent;

                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IAgent>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await AgentServices.create(itemForm.value);

            if (!result.error) {
                const [hydratedAgent] = await hydrateAgents([result.data as IAgentApiResponse]);
                current.value = hydratedAgent;
            }
        }

        return result;
    }

    function toForm(agent: IAgent): IAgentForm {
        const form = {} as IAgentForm;
        Object.assign(form, agent);

        // Clé étrangère avec fallback
        form.centerId = agent.center?.id ?? (agent as any).centerId;

        return form;
    }

    // permet d'initialiser un objet vide pour le formulaire
    function newForm(lienId: number): IAgentForm {
        itemForm.value = {
            centerId: lienId,
            id: 0,
            name: "",
            firstName: "",
            shortName: "",
            code: "",
            businessCode: "",
            startDate: new Date(),
            endDate: undefined,
            isLabAssist: false,
            isSampler: false
        } as IAgentForm;
        return itemForm.value;
    }

    // permet d'initialiser un objet vide pour les referentiels
    function newItem(lien: ICenter): IAgent {
        current.value = {
            id: 0,
            name: "",
            firstName: "",
            shortName: "",
            code: "",
            businessCode: "",
            startDate: new Date(),
            endDate: undefined,
            isLabAssist: false,
            isSampler: false,
            center: lien
        } as IAgent;
        return current.value;
    }

    // Retourner les propriétés et méthodes publiques
    return {
        // State
        datas,
        current,
        itemForm,

        // Getters
        getAgentById,
        getAgentByCode,
        isCodeDisponible,
        getAgentByCodeAndFunction,
        getAgentSamplerActifs,
        getAgentLabAssistActifs,
        getAgentActifs,

        // Actions
        hydrateAgents,
        load,
        get,
        searchStoreById,
        save,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajAgent", async (payload: IPayloadStore) => {
    const store = useAgentStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateAgents([payload.data as IAgentApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateAgents([payload.data as IAgentApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
